import type { StateTree } from '@makemydeal/dr-dash-types';
import type { paymentTypes } from '@makemydeal/dr-offer-redux';
import { OfferReduxConfig, actions as offerReduxActionTypes, paymentGridMiddleware } from '@makemydeal/dr-offer-redux';
import type { ReduxNext, ReduxStore } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import { SET_MANUAL_FIELD_OVERRIDE } from '../actionTypes/manualFieldOverridesActionTypes';
import { restoreManualFieldOverrides } from '../actions/manualFieldOverridesActionCreators';
import { setPaymentGridCarriedOverride } from '../actions/offerRedux';
import { RESTORED_ACTIVE_CELL_META } from '../constants';
import { invalidatesProgram } from '../programInvalidatingActions';
import { getManualFieldOverrides } from '../selectors/manualFieldOverridesSelectors';
import {
    getCurrentOfferType,
    getPaymentGridCarriedOverride,
    getPaymentGridCarriedOverrides,
    getPaymentGridDealColumnIndex,
    getPaymentGridTerms,
    isAspPaymentGridRedesignActive
} from '../selectors/offerRedux';
import { getExplicitDealProgram } from '../selectors/programsSelectors';

let cachedPaymentGridMiddleware: ((store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => void) | undefined;

const ACTIVE_COLUMN_ACTIONS: ReadonlySet<string> = new Set([
    offerReduxActionTypes.SET_PAYMENT_GRID_ACTIVE_CELL,
    offerReduxActionTypes.SELECTED_TERM
]);

const forward = (store: ReduxStore<StateTree>, next: ReduxNext, action: AnyFSA) => {
    if (cachedPaymentGridMiddleware) {
        cachedPaymentGridMiddleware(store)(next)(action);
    } else {
        next(action);
    }
};

const CARRIED_HALF_BY_DEAL_SCREEN_FIELD: Readonly<Record<string, 'sellRate' | 'residual'>> = {
    sellRate: 'sellRate',
    baseResidual: 'residual'
};

const isOverrideIndicatorActive = (state: StateTree, offerType: ReturnType<typeof getCurrentOfferType>) =>
    featureToggleSelectors.areFeatureTogglesAvailable(state) &&
    featureToggleSelectors.isPgOverrideIndicatorEnabled(state) &&
    isAspPaymentGridRedesignActive(state, offerType);

const isColumnUnstamp = (action: AnyFSA) =>
    action.type === offerReduxActionTypes.SET_PAYMENT_GRID_ACTIVE_CELL &&
    action.payload.axis === 'column' &&
    action.payload.index == null;

const forwardCarryingDealScreenOverrides = (store: ReduxStore<StateTree>, next: ReduxNext, action: AnyFSA) => {
    const before = store.getState();
    const offerType = getCurrentOfferType(before);
    if (action.meta?.[RESTORED_ACTIVE_CELL_META] || isColumnUnstamp(action) || !isOverrideIndicatorActive(before, offerType)) {
        forward(store, next, action);
        return;
    }

    const leftColumnIndex = getPaymentGridDealColumnIndex(before, offerType);
    const leftTerm = leftColumnIndex == null ? undefined : getPaymentGridTerms(before, offerType)[leftColumnIndex];
    const dealScreenOverridesBefore = getManualFieldOverrides(before);

    forward(store, next, action);

    if (leftColumnIndex == null || leftTerm == null) return;
    const after = store.getState();
    if (getCurrentOfferType(after) !== offerType) return;
    if (getPaymentGridTerms(after, offerType)[leftColumnIndex] !== leftTerm) return;

    const arrivedColumnIndex = getPaymentGridDealColumnIndex(after, offerType);
    const dealScreenOverridesAfter = getManualFieldOverrides(after);
    const hasLeftColumn = arrivedColumnIndex !== leftColumnIndex;
    const isCarried = (field: string) =>
        dealScreenOverridesBefore.includes(field) && (hasLeftColumn || !dealScreenOverridesAfter.includes(field));

    const carried = { sellRate: isCarried('sellRate'), residual: isCarried('baseResidual') };
    const carriedOverride =
        carried.sellRate || carried.residual
            ? getPaymentGridCarriedOverride(after, offerType, leftColumnIndex, carried)
            : undefined;
    if (!carriedOverride) return;

    if (hasLeftColumn) {
        store.dispatch(setPaymentGridCarriedOverride(offerType, leftColumnIndex, carriedOverride));
        return;
    }

    const restored = new Set<string>();
    if (carriedOverride.sellRate !== undefined) restored.add('sellRate');
    if (carriedOverride.residual !== undefined) restored.add('baseResidual');
    store.dispatch(
        restoreManualFieldOverrides([
            ...dealScreenOverridesBefore.filter((field) => dealScreenOverridesAfter.includes(field) || restored.has(field)),
            ...dealScreenOverridesAfter.filter((field) => !dealScreenOverridesBefore.includes(field))
        ])
    );
};

const forwardReleasingCarriedOverride = (store: ReduxStore<StateTree>, next: ReduxNext, action: AnyFSA) => {
    forward(store, next, action);

    const half = CARRIED_HALF_BY_DEAL_SCREEN_FIELD[action.payload as string];
    if (!half) return;
    const state = store.getState();
    const offerType = getCurrentOfferType(state);
    if (!isOverrideIndicatorActive(state, offerType)) return;

    const dealColumnIndex = getPaymentGridDealColumnIndex(state, offerType);
    if (dealColumnIndex == null) return;
    const carried = getPaymentGridCarriedOverrides(state, offerType)?.[dealColumnIndex];
    if (carried?.[half] === undefined) return;

    const remaining = { ...carried };
    delete remaining[half];
    const hasRemainingHalf = remaining.sellRate !== undefined || remaining.residual !== undefined;
    store.dispatch(setPaymentGridCarriedOverride(offerType, dealColumnIndex, hasRemainingHalf ? remaining : undefined));
};

export const middleware = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    if (action.type === initActionTypes.INIT_PENCIL_SUCCESS) {
        const config = OfferReduxConfig.getInstance();
        config.setConfig('isTermIdWithDownPayment', true);

        cachedPaymentGridMiddleware = paymentGridMiddleware({
            selectors: { getExplicitLenderLock: getExplicitDealProgram, invalidatesProgram }
        } as paymentTypes.IPaymentConfig);
    }

    if (ACTIVE_COLUMN_ACTIONS.has(action.type)) {
        forwardCarryingDealScreenOverrides(store, next, action);
    } else if (action.type === SET_MANUAL_FIELD_OVERRIDE) {
        forwardReleasingCarriedOverride(store, next, action);
    } else {
        forward(store, next, action);
    }
};
